// POS startup bootstrap: discount schedules + sysconfig flags.
//
// Backed by GET /v2/stores/{sid}/registers/{rid}/pos/bootstrap. The FE
// caches the response on POS open and refreshes on a long timer (default
// 30 minutes). Used for optimistic upsell hints ("buy 1 more for the
// 3-for-$30 deal") and pricing-mode UI; NOT for committed prices --
// that's /cart/preview's job.
//
// See `hypnotoad/handlers/api/v2/bootstrap.py` for the server side.

import { useQuery } from '@tanstack/react-query'
import { v2, v2api } from './v2'
import { useSessionStore } from '@/stores/sessionStore'

// ---------------------------------------------------------------------------
// Wire types -- match `_serialize_schedule` etc. in bootstrap.py
// ---------------------------------------------------------------------------

export interface DiscountTier {
  id: number
  qty_from: string | number | null
  qty: string | number | null
  discount_amount: string | number | null
}

export interface DiscountDay {
  /** 0=Mon..6=Sun, mirrors DiscountScheduleDaysOfWeek.day_of_week. */
  day: number | null
  start: string | null
  finish: string | null
  is_enabled: boolean
}

export interface DiscountScheduleSummary {
  id: number
  title: string | null
  description: string | null
  discount_type: number
  discount_amount: string | number | null
  points_multiplier: string | number | null
  discount_code: string | null
  /** null = applies to every customer group. */
  customer_group_id: number | null
  date_start: string | null
  date_finish: string | null
  for_delivery: boolean
  rev: number | null
  product_ids: number[]
  category_ids: number[]
  tiers: DiscountTier[]
  days_of_week: DiscountDay[]
}

export interface BootstrapSysConfig {
  /** When true, find_one picks the largest discount across overlapping
   *  schedules rather than the most recently created one. Affects which
   *  schedule the FE renders as "winning" in upsell hints. */
  'system.use_better_discount': boolean | null
  /** When true, scheduled discounts don't apply on the POS at all. */
  'pos.discounts.disabled': boolean | null
  /** Numeric flag controlling how discounts render on receipts. */
  'system.discount_receipt_mode': number | null
}

export interface BootstrapResponse {
  discount_schedules: DiscountScheduleSummary[]
  sysconfig: BootstrapSysConfig
  cache_version: string
  /** Fully-resolved logo URL (MEDIA_URL/CDN already prefixed) for the
   *  customer-facing display. Resolved server-side with priority
   *  register → store → system → default; the FE drops it straight into
   *  <img src> without any URL-stitching. `null` only if literally
   *  every level is unset (shouldn't happen — there's always a system
   *  default). Refetch when `cache_version` changes. */
  display_logo: string | null
  // Phase 2 placeholders -- always present, currently always empty.
  mix_match_rules: unknown[]
  price_groups: unknown[]
  customer_groups: unknown[]
  tax_rates_by_category: unknown[]
  deposit_recycling_fees: unknown[]
}

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export const bootstrapKeys = {
  all: ['pos-bootstrap'] as const,
  current: (storeId?: number, registerId?: number) =>
    [...bootstrapKeys.all, storeId ?? null, registerId ?? null] as const,
}

/** Default refresh interval (ms). Long enough that the BE isn't constantly
 *  serving the same payload; short enough that admin promo edits become
 *  visible within the cashier's shift. */
const DEFAULT_REFRESH_MS = 30 * 60 * 1000  // 30 minutes

/**
 * Fetch and cache the POS bootstrap. Call this once at the top of the POS
 * route; downstream components consume `data` to render upsell hints.
 *
 * The query key is scoped by store + register (read from the session
 * store via the `v2()` URL helper), so switching registers refreshes.
 */
export function usePosBootstrap(opts: {
  enabled?: boolean
  refreshMs?: number
} = {}) {
  // Read store/register IDs reactively so a register switch flips the
  // query key, naturally invalidating the cached bootstrap. The previous
  // implementation keyed on `bootstrapKeys.all` which is store/register-
  // agnostic, so register switches reused the prior register's payload
  // until the staleTime ticked.
  const storeId = useSessionStore((s) => s.selectedStore?.id ?? null)
  const registerId = useSessionStore((s) => s.selectedRegister?.id ?? null)
  return useQuery({
    queryKey: bootstrapKeys.current(storeId ?? undefined, registerId ?? undefined),
    queryFn: async (): Promise<BootstrapResponse> => {
      return v2api.get<BootstrapResponse>(v2('/pos/bootstrap'))
    },
    enabled: (opts.enabled ?? true) && storeId !== null && registerId !== null,
    staleTime: opts.refreshMs ?? DEFAULT_REFRESH_MS,
    refetchInterval: opts.refreshMs ?? DEFAULT_REFRESH_MS,
    // The FE survives a missing bootstrap fine -- it just can't render
    // upsell hints. Don't retry hard on transient failures.
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

// ---------------------------------------------------------------------------
// Helpers for upsell-hint rendering
// ---------------------------------------------------------------------------

/**
 * Filter the bootstrap's schedules down to the ones that *could* apply to
 * a given (product_id, category_ids, customer_group_id) tuple at the
 * current local time. Cheap client-side prune so upsell prompts can
 * render without a server round-trip.
 *
 * IMPORTANT: this is pruning, not winner-picking. The actual winning
 * schedule for each line is decided by the BE in /cart/preview via
 * `DiscountSchedule.find_one`, which honors `system.use_better_discount`
 * and a few other knobs the FE doesn't fully model. Use this to populate
 * "available offers" lists; never to compute the price the customer pays.
 */
export function applicableSchedules(
  all: DiscountScheduleSummary[],
  args: {
    product_id: number
    category_ids?: number[]
    customer_group_id?: number | null
    now?: Date
  },
): DiscountScheduleSummary[] {
  const now = args.now ?? new Date()
  const today = now.toISOString().slice(0, 10)
  const dow = (now.getDay() + 6) % 7  // JS Sun=0 → Mon=0 to match DB
  const time = now.toTimeString().slice(0, 8)
  return all.filter((s) => {
    if (s.date_start && s.date_start > today) return false
    if (s.date_finish && s.date_finish < today) return false
    if (
      s.customer_group_id !== null
      && s.customer_group_id !== (args.customer_group_id ?? null)
    ) return false
    const productMatch =
      s.product_ids.length === 0 || s.product_ids.includes(args.product_id)
    const categoryMatch =
      s.category_ids.length === 0
      || (args.category_ids ?? []).some((c) => s.category_ids.includes(c))
    if (!productMatch && !categoryMatch) {
      // No product OR category matched. (Empty arrays mean "applies to
      // all", so an empty-product + empty-category schedule slips
      // through both legs of the OR.)
      if (s.product_ids.length > 0 || s.category_ids.length > 0) return false
    }
    if (s.days_of_week.length > 0) {
      const dayRule = s.days_of_week.find((d) => d.day === dow && d.is_enabled)
      if (!dayRule) return false
      if (dayRule.start && time < dayRule.start) return false
      if (dayRule.finish && time > dayRule.finish) return false
    }
    return true
  })
}
